import { For, Show } from "solid-js";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { formatDay } from "../calendarRenderUtils";
import type { LinearIssue } from "./linearIssuesSchema";

// URLs arrive from the customer's workspace and are untrusted; only https is
// safe to put in an href.
export const safeLinearUrl = (url: string | undefined): string | undefined =>
  url?.startsWith("https://") ? url : undefined;

// Target and start dates are calendar days with no zone. A bare "2025-01-15"
// parses as UTC midnight and renders as the 14th anywhere west of UTC, so the
// time suffix pins it to local midnight instead.
export const formatLinearDate = (date: string): string => formatDay(`${date}T00:00`);

const NEUTRAL_PILL = "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";

// Customers rename workflow states freely, so the pill colour keys off Linear's
// fixed state category instead of the state's name. Linear spells the last one
// "canceled"; the tool contract writes it "cancelled", so both map here.
const STATE_PILL_CLASSES: Record<string, string> = {
  triage: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  backlog: NEUTRAL_PILL,
  unstarted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  started: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  canceled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

// Linear's priority scale runs 1 Urgent through 4 Low; 0 means "no priority",
// not the highest. A list row skips 0 as noise, but a confirm card shows it,
// because setting priority to 0 there is a deliberate write.
const PRIORITY_LABEL_KEYS: Record<number, string> = {
  0: "linear_priority.none",
  1: "linear_priority.urgent",
  2: "linear_priority.high",
  3: "linear_priority.medium",
  4: "linear_priority.low",
};

export const priorityLabelKey = (priority: number): string | undefined => PRIORITY_LABEL_KEYS[priority];

export const LinearIssueMeta = (props: { issue: LinearIssue }) => {
  const rt = useChatRuntime();

  return (
    <div class="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
      <span class={`shrink-0 rounded px-1.5 py-0.5 ${STATE_PILL_CLASSES[props.issue.stateType] ?? NEUTRAL_PILL}`}>
        {props.issue.state}
      </span>
      <Show when={props.issue.priority > 0 && priorityLabelKey(props.issue.priority)}>
        {(key) => <span>{rt.t(key())}</span>}
      </Show>
      <Show when={props.issue.assignee}>{(assignee) => <span class="truncate">{assignee()}</span>}</Show>
      <span class="truncate">{props.issue.team}</span>
      <Show when={props.issue.project}>{(project) => <span class="truncate">{project()}</span>}</Show>
      <For each={props.issue.labels}>
        {(label) => <span class="truncate rounded bg-inset px-1.5 py-0.5">{label}</span>}
      </For>
    </div>
  );
};
